import React from "react";
import { Table, Popconfirm, Tag } from "antd";
import { ColumnType } from "antd/es/table";
import { RiDeleteBin6Line } from "react-icons/ri";
import { FiEdit3 } from "react-icons/fi";
import { IRecipientsListResponse } from "../../../types/api/recipients";
import { renderIcon, renderTableText } from "../../../lib/utils";
import RecipientsGroupCard from "../../components/reuseComp/RecipientsGroupCard";
import { useNavigate } from "react-router-dom";
import MyPagination from "../../../views/components/Pagination";

const EditIcon = renderIcon(FiEdit3);
const DeleteIcon = renderIcon(RiDeleteBin6Line);

interface RecipientsTableProps {
  data?: IRecipientsListResponse[];
  handleDelete?: (id: string) => void;
  loading?: boolean;
  DEFAULT_NUQS_CONFIG?: any;
  qData?: any;
  refetch?: any;
}

const RecipientsTable: React.FC<RecipientsTableProps> = ({
  data = [],
  handleDelete = () => {},
  loading = false,
  DEFAULT_NUQS_CONFIG,
  qData,
  refetch,
}) => {
  const navigate = useNavigate();

  const columns: ColumnType<IRecipientsListResponse>[] = [
    {
      title: "Group Name",
      dataIndex: "groupName",
      key: "groupName",
      render: (v) => renderTableText(v),
    },

    {
      title: "Emails",
      dataIndex: "emails",
      key: "emails",
      render: (emails: string[]) =>
        emails?.length ? `${emails[0]}${emails.length > 1 ? ` (+${emails.length - 1})` : ""}` : "-",
    },

    {
      title: "Job Linked",
      dataIndex: "jobLinked",
      key: "jobLinked",
      render: (jobs: string[]) =>
        jobs?.length ? `${jobs[0]}${jobs.length > 1 ? ` (+${jobs.length - 1})` : ""}` : "-",
    },
     {
      title: "Order",
      dataIndex: "order",
      key: "order",
      render : (v) => renderTableText(v)
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: boolean) => (
        <Tag color={status ? "green" : ""} bordered>
          {status ? "Active" : "In Active"}
        </Tag>
      ),
    },

    {
      title: "Action",
      key: "action",
      align: "right",
      render: (_: any, row: any) => (
        <div className="flex items-center justify-end gap-3 pr-3">
          <EditIcon
            className="cursor-pointer text-primary text-[20px]"
            onClick={() => navigate(`/recipients/edit/${row?._id}`)}
          />

          <Popconfirm
            title="Are you sure to delete this row?"
            onConfirm={() => handleDelete(row?._id)}
            okText="Yes"
            cancelText="No"
          >
            <DeleteIcon className="cursor-pointer text-primary hover:text-red-600 text-[20px]" />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Desktop Table */}
      <div className="hidden md:block">
        <Table
          dataSource={data}
          columns={columns}
          rowKey={(item: any) => item?._id}
          loading={loading}
          pagination={false}
          size="middle"
          bordered={false}
          className="custom-table-class rounded-lg border border-gray-200 shadow-md"
          expandable={{
            expandRowByClick: false,
            expandedRowRender: (record) => (
              <div className="bg-gray-100 p-4 rounded-md">
                {/* Emails */}
                <div className="mb-3">
                  <p className="font-semibold text-gray-700 mb-1">Emails</p>
                  <div className="flex flex-wrap gap-2">
                    {record.emails?.map((email : any, idx : any) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-white text-primary border rounded text-sm"
                      >
                        {email}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Job Linked */}
                <div>
                  <p className="font-semibold text-gray-700 mb-1">Job Linked</p>
                  <div className="flex flex-wrap gap-2">
                    {record.jobLinked?.map((job :any, idx :any) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-white text-primary border rounded text-sm"
                      >
                        {job}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ),
          }}
          footer={() => (
            <MyPagination
              qData={qData}
              onChange={refetch}
              total={qData?.total}
              DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG}
            />
          )}
        />
      </div>

      {/* Mobile View */}
      <div className="grid gap-4 md:hidden">
        {data?.map((v) => (
          <RecipientsGroupCard
            key={v?._id}
            data={v}
            handleDelete={handleDelete}
          />
        ))}
      </div>
    </div>
  );
};

export default RecipientsTable;
